import React, { useState } from 'react';
import { View, Text, StyleSheet, Modal, Pressable, ScrollView, Image } from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { ChildProfile } from '../../interface/User';

interface ChildSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (selectedChildIds: string[], action: 'register' | 'unregister') => void;
  children: ChildProfile[];
  alreadyRegisteredChildIds?: string[];
}

const ChildSelectionModal: React.FC<ChildSelectionModalProps> = ({
  visible,
  onClose,
  onConfirm,
  children,
  alreadyRegisteredChildIds = [],
}) => {
  // Selected children represents the current state (checked = registered)
  const [selectedChildren, setSelectedChildren] = useState<string[]>([]);

  // Initialize with already registered children when modal opens
  React.useEffect(() => {
    if (visible) {
      setSelectedChildren(alreadyRegisteredChildIds);
    }
  }, [visible, alreadyRegisteredChildIds]);

  const toggleChild = (childId: string) => {
    if (selectedChildren.includes(childId)) {
      setSelectedChildren(selectedChildren.filter(id => id !== childId));
    } else {
      setSelectedChildren([...selectedChildren, childId]);
    }
  };

  const handleConfirm = () => {
    // Determine which children need to be registered (newly selected)
    const childrenToRegister = selectedChildren.filter(
      id => !alreadyRegisteredChildIds.includes(id)
    );
    
    // Determine which children need to be unregistered (were registered but now deselected)
    const childrenToUnregister = alreadyRegisteredChildIds.filter(
      id => !selectedChildren.includes(id)
    );
    
    // Process registrations
    if (childrenToRegister.length > 0) {
      onConfirm(childrenToRegister, 'register');
    }
    
    // Process unregistrations
    if (childrenToUnregister.length > 0) {
      onConfirm(childrenToUnregister, 'unregister');
    }
    
    setSelectedChildren([]);
    onClose();
  };

  const handleCancel = () => {
    setSelectedChildren([]);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={handleCancel}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Select Children to Register</Text>
            <Pressable onPress={handleCancel} style={styles.closeButton}>
              <MaterialCommunityIcons name="close" size={24} color="#666" />
            </Pressable>
          </View>

          {/* Content */}
          <ScrollView style={styles.content}>
            <Text style={styles.subtitle}>
              Select children to register for this session
            </Text>

            {children.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="account-child" size={48} color="#ccc" />
                <Text style={styles.emptyText}>No children in your profile</Text>
                <Text style={styles.emptySubtext}>
                  Add children to your family profile to register them for sessions
                </Text>
              </View>
            ) : (
              <View style={styles.childrenList}>
                {children.map((child) => {
                  const isSelected = selectedChildren.includes(child.childId);
                  const isAlreadyRegistered = alreadyRegisteredChildIds.includes(child.childId);
                  
                  return (
                    <Pressable
                      key={child.childId}
                      style={[
                        styles.childCard,
                        isSelected && styles.childCardSelected,
                      ]}
                      onPress={() => toggleChild(child.childId)}
                    >
                      <View style={styles.childInfo}>
                        {child.profileImage ? (
                          <Image
                            source={{ uri: child.profileImage }}
                            style={styles.avatarImage}
                            resizeMode="cover"
                          />
                        ) : (
                          <View style={styles.avatarPlaceholder}>
                            <Text style={styles.avatarText}>
                              {child.firstName?.charAt(0)}{child.lastName?.charAt(0)}
                            </Text>
                          </View>
                        )}
                        <View style={styles.childDetails}>
                          <Text style={styles.childName}>
                            {child.firstName} {child.lastName}
                          </Text>
                          <View style={styles.childStatusRow}>
                            <Text style={styles.childAge}>Age: {child.age}</Text>
                            {isAlreadyRegistered && (
                              <View style={styles.registeredBadge}>
                                <MaterialCommunityIcons name="check-circle" size={14} color="#28A745" />
                                <Text style={styles.registeredText}>Registered</Text>
                              </View>
                            )}
                          </View>
                        </View>
                      </View>
                      
                      <View style={[
                        styles.checkbox, 
                        isSelected && styles.checkboxSelected,
                      ]}>
                        {isSelected && (
                          <MaterialCommunityIcons 
                            name="check" 
                            size={18} 
                            color="#fff" 
                          />
                        )}
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </ScrollView>

          {/* Footer */}
          {children.length > 0 && (
            <View style={styles.footer}>
              <Pressable
                style={styles.cancelButton}
                onPress={handleCancel}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </Pressable>
              
              <Pressable
                style={styles.confirmButton}
                onPress={handleConfirm}
              >
                <Text style={styles.confirmButtonText}>
                  Confirm {selectedChildren.length > 0 ? `(${selectedChildren.length})` : ''}
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#222',
    flex: 1,
  },
  closeButton: {
    padding: 4,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 20,
    lineHeight: 20,
  },
  childrenList: {
    gap: 12,
  },
  childCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E0E0E0',
    backgroundColor: '#F8F8F8',
  },
  childCardSelected: {
    borderColor: '#3C8248',
    backgroundColor: '#e8f5e8',
  },
  childInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatarImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginRight: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  avatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#3C8248',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  avatarText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  childDetails: {
    flex: 1,
  },
  childName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#222',
    marginBottom: 4,
  },
  childStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  childAge: {
    fontSize: 14,
    color: '#666',
  },
  registeredBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: '#e8f5e8',
  },
  registeredText: {
    fontSize: 11,
    color: '#28A745',
    fontWeight: '600',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#ccc',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#3C8248',
    borderColor: '#3C8248',
  },
  emptyState: {
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    marginTop: 8,
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ccc',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  confirmButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#3C8248',
    alignItems: 'center',
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default ChildSelectionModal;
